import React, { useState, useRef, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom'
import { XMarkIcon, TrashIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import type { DynamicFilter, FilterConfig, FilterOperator, FilterOption } from '@/types/filters'
import { getOperatorsForType, getOperatorLabel } from '@/types/filters'

interface NewAdvancedFiltersModalProps {
  isOpen: boolean
  onClose: () => void
  filters: DynamicFilter[]
  availableFilters: FilterConfig[]
  onAddFilter: (columnId: string) => void
  onUpdateFilter: (filterId: string, updates: Partial<DynamicFilter>) => void
  onRemoveFilter: (filterId: string) => void
  onClearAllFilters: () => void
  onApplyFilters: () => void
}

const NewAdvancedFiltersModal: React.FC<NewAdvancedFiltersModalProps> = ({
  isOpen,
  onClose,
  filters,
  availableFilters,
  onAddFilter,
  onUpdateFilter,
  onRemoveFilter,
  onClearAllFilters,
  onApplyFilters
}) => {
  if (!isOpen) return null

  // Campos disponíveis para adicionar (que ainda não estão sendo filtrados)
  const availableToAdd = availableFilters.filter(
    config => !filters.some(f => f.columnId === config.columnId)
  )

  const handleAddFilters = (columnIds: string[]) => {
    columnIds.forEach(columnId => {
      if (columnId && !filters.some(f => f.columnId === columnId)) {
        onAddFilter(columnId)
      }
    })
  }

  const handleApply = () => {
    onApplyFilters()
    onClose()
  }

  const groupedFilters = filters.reduce((acc, filter) => {
    const config = availableFilters.find(c => c.columnId === filter.columnId)
    const category = config?.category || 'Outros'
    
    if (!acc[category]) acc[category] = []
    acc[category].push(filter)
    
    return acc
  }, {} as Record<string, DynamicFilter[]>)

  const modalContent = (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4" style={{ position: 'fixed', inset: 0 }}>
      <div className="relative mx-auto border w-full max-w-6xl h-[90vh] shadow-lg rounded-lg bg-white flex flex-col p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Filtros Avançados</h3>
            <p className="text-sm text-gray-500 mt-1">
              Configure filtros personalizados para suas colunas
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Add New Filters */}
        {availableToAdd.length > 0 && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Adicionar Filtros</h4>
            <MultiSelectInput
              options={availableToAdd.map(config => ({
                value: config.columnId,
                label: `${config.label} (${config.category})`
              }))}
              values={[]}
              onChange={handleAddFilters}
              placeholder="Selecione as colunas para filtrar..."
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-2">
              Selecione múltiplas colunas para adicionar filtros automaticamente
            </p>
          </div>
        )}

        {/* Active Filters */}
        <div className="flex-1 overflow-y-auto">
          {Object.keys(groupedFilters).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nenhum filtro configurado</p>
              <p className="text-sm">Adicione filtros usando o seletor acima</p>
            </div>
          ) : (
            Object.entries(groupedFilters).map(([category, categoryFilters]) => (
              <div key={category} className="mb-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3 border-b pb-1">
                  {category}
                </h4>
                <div className="space-y-3">
                  {categoryFilters.map(filter => (
                    <FilterRow
                      key={filter.id}
                      filter={filter}
                      config={availableFilters.find(c => c.columnId === filter.columnId)!}
                      onUpdate={(updates) => onUpdateFilter(filter.id, updates)}
                      onRemove={() => onRemoveFilter(filter.id)}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 flex justify-between items-center pt-4 border-t">
          <div className="flex gap-2">
            <button
              onClick={onClearAllFilters}
              className="btn-secondary text-sm"
              disabled={filters.length === 0}
            >
              Limpar Todos
            </button>
          </div>
          
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
            <button onClick={handleApply} className="btn-primary">
              Aplicar Filtros ({filters.filter(f => f.enabled).length})
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(modalContent, document.body)
}

// Componente individual de filtro
interface FilterRowProps {
  filter: DynamicFilter
  config: FilterConfig
  onUpdate: (updates: Partial<DynamicFilter>) => void
  onRemove: () => void
}

const FilterRow: React.FC<FilterRowProps> = ({ filter, config, onUpdate, onRemove }) => {
  const availableOperators = config.operators.length > 0
    ? config.operators
    : getOperatorsForType(filter.type)
  
  const renderValueInput = () => {
    const baseClassName = "input-primary flex-1"
    
    switch (filter.type) {
      case 'text':
        return (
          <input
            type="text"
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            placeholder={config.placeholder}
            className={baseClassName}
          />
        )
      
      case 'select':
        return (
          <select
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            className={baseClassName}
          >
            <option value="">Selecione...</option>
            {config.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label} {option.count ? `(${option.count})` : ''}
              </option>
            ))}
          </select>
        )
      
      case 'multiselect':
        return (
          <MultiSelectInput
            options={config.options || []}
            values={Array.isArray(filter.value) ? filter.value : []}
            onChange={(values) => onUpdate({ value: values })}
            placeholder="Selecione múltiplas opções..."
            className={baseClassName}
          />
        )
      
      case 'number':
        return (
          <input
            type="number"
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: parseFloat(e.target.value) || null })}
            className={baseClassName}
          />
        )
      
      case 'date':
        return (
          <input
            type="date"
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            className={baseClassName}
          />
        )

      case 'boolean':
        return (
          <select
            value={filter.value ?? ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            className={baseClassName}
          >
            <option value="">Selecione...</option>
            {(config.options || [
              { value: 'true', label: 'Sim' },
              { value: 'false', label: 'Não' }
            ]).map(option => (
              <option key={option.value} value={option.value}>
                {option.label} {option.count ? `(${option.count})` : ''}
              </option>
            ))}
          </select>
        )
      
      default:
        return (
          <input
            type="text"
            value={filter.value || ''}
            onChange={(e) => onUpdate({ value: e.target.value })}
            className={baseClassName}
          />
        )
    }
  }

  return (
    <div className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-lg">
      {/* Enable/Disable Toggle */}
      <input
        type="checkbox"
        checked={filter.enabled}
        onChange={(e) => onUpdate({ enabled: e.target.checked })}
        className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
      />
      
      {/* Column Name */}
      <div className="min-w-0 w-32">
        <span className="text-sm font-medium text-gray-900 truncate block">
          {filter.label}
        </span>
      </div>
      
      {/* Operator */}
      <div className="w-40">
        <select
          value={filter.operator}
          onChange={(e) => onUpdate({ operator: e.target.value as FilterOperator })}
          className="input-primary w-full text-sm"
        >
          {availableOperators.map(op => (
            <option key={op} value={op}>
              {getOperatorLabel(op)}
            </option>
          ))}
        </select>
      </div>
      
      {/* Value Input */}
      <div className="flex-1">
        {renderValueInput()}
      </div>
      
      {/* Remove Button */}
      <button
        onClick={onRemove}
        className="text-gray-400 hover:text-red-500 p-1"
        title="Remover filtro"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  )
}

// Componente MultiSelectInput
interface MultiSelectInputProps {
  options: FilterOption[]
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  className?: string
}

const MultiSelectInput: React.FC<MultiSelectInputProps> = ({
  options,
  values,
  onChange,
  placeholder = "Selecione opções...",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom')
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filtrar opções baseado no termo de busca
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options
    return options.filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [options, searchTerm])

  const toggleOption = (optionValue: string) => {
    const newValues = values.includes(optionValue)
      ? values.filter(v => v !== optionValue)
      : [...values, optionValue]
    onChange(newValues)
  }

  const getDisplayText = () => {
    if (values.length === 0) return placeholder
    if (values.length === 1) {
      const option = options.find(o => o.value === values[0])
      return option?.label || values[0]
    }
    return `${values.length} opções selecionadas`
  }

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange([])
  }

  const handleToggleDropdown = () => {
    if (!isOpen) {
      // Calcular posição antes de abrir
      const element = dropdownRef.current
      if (element) {
        const rect = element.getBoundingClientRect()
        const windowHeight = window.innerHeight
        const spaceBelow = windowHeight - rect.bottom
        const spaceAbove = rect.top
        
        // Se não há espaço suficiente abaixo (menos de 250px), abrir para cima
        setDropdownPosition(spaceBelow < 250 && spaceAbove > spaceBelow ? 'top' : 'bottom')
      }
      
      setSearchTerm('')
      setIsOpen(true)
      
      // Focus no input de busca após um pequeno delay
      setTimeout(() => {
        const searchInput = dropdownRef.current?.querySelector('input[type="text"]') as HTMLInputElement
        if (searchInput) {
          searchInput.focus()
        }
      }, 100)
    } else {
      setIsOpen(false)
    }
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Input/Button */}
      <div
        onClick={handleToggleDropdown}
        className="input-primary w-full flex items-center justify-between cursor-pointer px-3 py-2 min-h-[38px]"
      >
        <span className={`${values.length === 0 ? 'text-gray-400' : 'text-gray-900'} text-sm truncate`}>
          {getDisplayText()}
        </span>
        <div className="flex items-center gap-2 flex-shrink-0">
          {values.length > 0 && (
            <button
              onClick={clearAll}
              className="text-gray-400 hover:text-gray-600 text-sm font-semibold w-4 h-4 flex items-center justify-center rounded"
              title="Limpar seleção"
            >
              ×
            </button>
          )}
          <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute left-0 right-0 z-50 bg-white border border-gray-300 rounded-md shadow-lg ${
          dropdownPosition === 'top' 
            ? 'bottom-full mb-1' 
            : 'top-full mt-1'
        }`}>
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              placeholder="Buscar opções..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          
          {/* Options List */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                {searchTerm ? 'Nenhum resultado encontrado' : 'Nenhuma opção disponível'}
              </div>
            ) : (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  onClick={() => toggleOption(option.value)}
                  className="px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center gap-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={values.includes(option.value)}
                    onChange={() => {}} // Controlado pelo onClick do div
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>{option.label}</span>
                  {option.count && (
                    <span className="text-gray-400 ml-auto">({option.count})</span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default NewAdvancedFiltersModal
